/* eslint-disable no-unreachable */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import {
    withStyles,
    Typography,
    Button,
    CircularProgress
} from '@material-ui/core';
import { red } from '@material-ui/core/colors';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import BankPaymentForm from './BankPaymentForm';
import VoucherForm from './VoucherForm';
import ContentHeader from './ContentHeader';
import {
    formatTicketType,
    formatDate,
    formatPrice
} from '../helpers/formatters';
import {
    TRAVEL_PATTERN_DAY_NAMES,
    calculateSelectPrice,
    getReadablePriceSummary,
    parseTravelPattern
} from '../helpers/ticket';

const styles = {
    root: {
        textAlign: 'left',
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)'
    },
    error: {
        margin: '20px 0 0 5px',
        padding: '10px',
        color: red[500],
        minHeight: '20px',
        textAlign: 'center'
    },
    spinner: {
        marginLeft: 'calc(50% - 22px)',
        marginTop: '60px',
        marginBottom: '60px'
    },
    description: {
        padding: '10px',
        marginBottom: '20px',
        fontWeight: '100'
    },
    button: {
        height: '40px',
        marginTop: '10px',
        marginBottom: '10px'
    },
    passengerInfo: {
        padding: '10px',
        marginBottom: '20px',
        textAlign: 'center'
    },
    imagePreview: {
        minHeight: '56px',
        margin: '20px auto 0',
        width: '100%',
        maxWidth: '400px',
        display: 'flex',
        justifyContent: 'center',
        '& > img': {
            maxWidth: '100%'
        },
        '@media only screen and (max-device-width: 400px)': {
            margin: '0 auto 0'
        }
    }
};

class ConfirmBankPurchase extends Component {
    constructor(props) {
        super(props);

        this.state = {
            voucher: null,
            stripePromise: null,
            error: '',
            isLoading: true,
            allowEdit: true
        };
    }

    async componentDidMount() {
        const { client, paymentIntent: propPaymentIntent } = this.props;

        try {
            const stripePromise = loadStripe(
                process.env.REACT_APP_STRIPE_PUB_KEY,
                {
                    stripeAccount: client.stripe_account_id
                }
            );

            let paymentIntent = propPaymentIntent;
            if (!paymentIntent) {
                paymentIntent = await this.getPaymentIntent();
            }

            this.setState({
                stripePromise,
                paymentIntent,
                isLoading: false
            });
        } catch (err) {
            console.error('Error initializing payment', err);
            this.setState({
                isLoading: false,
                error:
                    'Error initializing payment.  Please refresh the page and try again.'
            });
        }
    }

    getPaymentIntent = async () => {
        const { passenger, ticket, client, service } = this.props;

        const currentSession = await Auth.currentSession();

        const headers = {
            Authorization: currentSession.getIdToken().getJwtToken()
        };

        const body = {
            confirm: false,
            payment_method: 'pay_by_bank',
            ticket,
            service,
            passenger: {
                ...passenger,
                ...(passenger.travelPattern && {
                    travel_pattern: passenger.travelPattern
                })
            },
            client
        };

        try {
            const paymentIntentResponse = await axios.post(
                `${process.env.REACT_APP_PAYMENT_API_ENDPOINT}/customer-ticket/bank-direct-payment-intent`,
                body,
                {
                    headers
                }
            );
            if (!paymentIntentResponse.data?.intent) {
                throw new Error(
                    `Invalid payment intent response: ${JSON.stringify(
                        paymentIntentResponse.data
                    )}`
                );
            }

            return paymentIntentResponse.data.intent;
        } catch (err) {
            console.error('Error creating payment intent', err);
            throw err;
        }
    };

    setVoucher = voucher => {
        this.setState({ voucher });
    };

    netPrice = price => {
        const { voucher } = this.state;
        if (voucher) {
            return price - voucher.value;
        }
        return price;
    };

    getPrice = (ticket, passenger, useZones, zone) => {
        if (ticket.type === 'flexi') {
            return `(${ticket.default_trips} trips x ${formatPrice(
                useZones ? ticket.zone_trip_prices[zone] : ticket.trip_price,
                ticket.currency
            )})`;
        }

        if (ticket.sub_type === 'select') {
            const price = calculateSelectPrice(
                passenger.travelPattern,
                ticket,
                zone
            );

            const totals = passenger.travelPattern
                .split('#')
                .map(pat => pat.split('_'))
                .reduce(
                    (acc, [day, amPm]) => {
                        const dayKey = TRAVEL_PATTERN_DAY_NAMES[
                            day
                        ].toLowerCase();
                        const journeysOnDay =
                            ticket.num_bookable_journeys[dayKey];

                        if (amPm) {
                            acc.singles += journeysOnDay;
                        } else {
                            acc.returns += journeysOnDay;
                        }

                        return acc;
                    },
                    { singles: 0, returns: 0 }
                );

            const singlePrice = useZones
                ? ticket.zone_prices[zone]
                : ticket.price;
            const returnPrice = useZones
                ? ticket.zone_return_prices[zone]
                : ticket.return_price;

            return `${formatPrice(
                price,
                ticket.currency
            )} (${getReadablePriceSummary(
                totals,
                singlePrice,
                returnPrice,
                ticket,
                { shortened: true }
            )})`;
        }

        return formatPrice(
            this.netPrice(useZones ? ticket.zone_prices[zone] : ticket.price),
            ticket.currency
        );
    };

    calcEndDate = (type, startDate) => {
        if (type === 'week') {
            const endDate = moment(startDate.toISOString());
            endDate.add(6, 'days').endOf('day');
            return endDate.format('ddd Do MMM YYYY');
        }

        if (type === '4_week') {
            const endDate = moment(startDate.toISOString());
            endDate
                .add(4, 'weeks')
                .subtract(1, 'days')
                .endOf('day');
            return endDate.format('ddd Do MMM YYYY');
        }

        if (type === 'return') {
            const endDate = moment(startDate.toISOString());
            endDate.endOf('day');
            return endDate.format('ddd Do MMM YYYY');
        }

        return 'Unknown';
    };

    render() {
        const {
            handleEdit,
            passenger,
            ticket,
            client,
            service,
            imagePreviewUrl,
            classes,
            buyAgain,
            disablePhoto,
            requireStudentId
        } = this.props;

        const {
            voucher,
            stripePromise,
            paymentIntent,
            isLoading,
            error,
            allowEdit
        } = this.state;

        if (isLoading) {
            return (
                <div className={classes.root}>
                    <CircularProgress
                        id="loading-spinner"
                        className={classes.spinner}
                    />
                </div>
            );
        }

        if (error) {
            return (
                <div className={classes.root}>
                    <Typography className={classes.error} variant="body2">
                        {error}
                    </Typography>
                </div>
            );
        }

        const isFlexiAndNotSingle =
            ticket.type === 'week' ||
            ticket.type === '4_week' ||
            ticket.type === 'return';

        const {
            firstName,
            lastName,
            zone,
            zoneStop,
            stop,
            dest,
            yr,
            startDate,
            startTime,
            id
        } = passenger;

        const travelPattern = ticket.travel_pattern || passenger.travelPattern;

        const useZones =
            ticket.pricing_model === 'Zonal' && ticket.zone_prices && zone;

        const elOptions = paymentIntent
            ? { clientSecret: paymentIntent.client_secret }
            : null;

        return (
            <>
                <div>
                    <div className={classes.root}>
                        <ContentHeader
                            text="Ticket details"
                            id="ticket-details-heading"
                        />
                        {!buyAgain && imagePreviewUrl && (
                            <div className={classes.imagePreview}>
                                <img src={imagePreviewUrl} alt="Ticket user" />
                            </div>
                        )}
                        <div className={classes.passengerInfo}>
                            <Typography variant="body2">
                                Passenger: {firstName} {lastName}
                            </Typography>
                            {requireStudentId && id && (
                                <Typography variant="body2">
                                    Student ID: {id}
                                </Typography>
                            )}
                            {stop && (
                                <Typography variant="body2">
                                    Bus stop: {stop}
                                </Typography>
                            )}
                            {zone && (
                                <Typography variant="body2">
                                    Zone: {zone}
                                </Typography>
                            )}
                            {zoneStop && (
                                <Typography variant="body2">
                                    Bus stop: {zoneStop}
                                </Typography>
                            )}
                            {dest && (
                                <Typography variant="body2">
                                    Destination: {dest}
                                </Typography>
                            )}
                            {yr && (
                                <Typography variant="body2">
                                    Year group:{' '}
                                    <span
                                        style={{ textTransform: 'capitalize' }}
                                    >
                                        {yr}
                                    </span>
                                </Typography>
                            )}
                            <Typography variant="body2">
                                Ticket:{' '}
                                {formatTicketType(
                                    service.name,
                                    ticket.type,
                                    ticket.sub_type,
                                    travelPattern, // TODO: reconcile with main PP
                                    ticket.name
                                )}
                            </Typography>
                            {/* @TODO: force-hiding the below until reconciled */}
                            {travelPattern && false && (
                                <Typography variant="body2">
                                    Valid only:{' '}
                                    {parseTravelPattern(travelPattern)}
                                </Typography>
                            )}
                            {ticket.start && (
                                <Typography variant="body2">
                                    Valid from: {formatDate(ticket.start)}
                                </Typography>
                            )}
                            {ticket.expiry && (
                                <Typography variant="body2">
                                    Valid until: {formatDate(ticket.expiry)}
                                </Typography>
                            )}
                            {startDate && startTime && (
                                <Typography variant="body2">
                                    Valid: {startDate.format('ddd Do MMM YYYY')}{' '}
                                    - {startTime.toUpperCase()} Only
                                </Typography>
                            )}
                            {startDate && isFlexiAndNotSingle && (
                                <>
                                    <Typography variant="body2">
                                        Valid from:{' '}
                                        {startDate.format('ddd Do MMM YYYY')}{' '}
                                    </Typography>
                                    <Typography variant="body2">
                                        Valid until:{' '}
                                        {this.calcEndDate(
                                            ticket.type,
                                            startDate
                                        )}
                                    </Typography>
                                </>
                            )}
                            <Typography variant="body2">
                                Price:{' '}
                                {this.getPrice(
                                    ticket,
                                    passenger,
                                    useZones,
                                    zone
                                )}
                            </Typography>
                            <Button
                                variant="outlined"
                                color="primary"
                                className={classes.button}
                                disabled={!allowEdit}
                                onClick={() =>
                                    handleEdit({
                                        paymentIntent
                                    })
                                }
                            >
                                Edit
                            </Button>
                        </div>
                    </div>
                    <div className={classes.root}>
                        <ContentHeader text="Voucher" id="voucher-heading" />
                        <VoucherForm
                            allowEdit={allowEdit}
                            client={client}
                            setVoucher={this.setVoucher}
                        />
                    </div>
                    <div className={classes.root}>
                        <ContentHeader text="Pay" id="pay-heading" />
                        {paymentIntent && (
                            <Elements
                                stripe={stripePromise}
                                options={elOptions}
                            >
                                <BankPaymentForm
                                    ticket={ticket}
                                    service={service}
                                    passenger={passenger}
                                    client={client}
                                    voucher={voucher}
                                    buyAgain={buyAgain}
                                    disablePhoto={disablePhoto}
                                    paymentIntent={paymentIntent}
                                    setAllowEdit={value => {
                                        this.setState({ allowEdit: value });
                                    }}
                                />
                            </Elements>
                        )}
                    </div>
                </div>
            </>
        );
    }
}

ConfirmBankPurchase.propTypes = {
    handleEdit: PropTypes.func.isRequired,
    passenger: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        photo: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        stop: PropTypes.string
    }).isRequired,
    ticket: PropTypes.shape({
        start: PropTypes.string,
        expiry: PropTypes.string,
        price: PropTypes.string
    }).isRequired,
    client: PropTypes.shape().isRequired,
    service: PropTypes.shape().isRequired,
    imagePreviewUrl: PropTypes.string,
    classes: PropTypes.shape().isRequired,
    buyAgain: PropTypes.bool,
    disablePhoto: PropTypes.bool,
    requireStudentId: PropTypes.bool,
    paymentIntent: PropTypes.shape()
};

ConfirmBankPurchase.defaultProps = {
    imagePreviewUrl: '',
    buyAgain: false,
    disablePhoto: false,
    requireStudentId: false,
    paymentIntent: null
};

export default withStyles(styles)(ConfirmBankPurchase);
